/**
 * Interromper o Brain falando (B7, decisão do usuário em 13/09/2026): enquanto a voz do
 * Brain toca, o microfone continua ouvindo; se a pessoa fala, a voz para, o Brain ouve de
 * novo e responde à última fala, na mesma conversa.
 *
 * Aqui só a DECISÃO, sem áudio, para ser testada (`tests/unit/brain-interrupcao.test.ts`):
 *
 * 1. **Calibração.** Os primeiros `CALIBRACAO_MS` de cada fala do Brain só medem: o que
 *    chega ao microfone ali é o eco dele, e esse nível vira a base do limiar.
 * 2. **Energia e duração.** Mesmo com `echoCancellation`, a voz do Brain vaza no
 *    microfone. Enquanto ela toca, o limiar sobe (`LIMIAR_TOCANDO`, ou 4× o fundo com o
 *    eco) e a fala da pessoa precisa durar `INTERRUPCAO_MINIMA_MS` seguidos — tosse,
 *    clique e "hã" curtos não param nada. Buracos de até `BURACO_TOLERADO_MS` entre
 *    sílabas não zeram a contagem.
 * 3. **O que foi dito.** Depois de pausar a voz e transcrever: vazio, ruído, as
 *    alucinações conhecidas do Whisper em silêncio, ou o eco das próprias palavras do
 *    Brain → a voz RETOMA de onde parou. "Para", "chega", "espera" → a voz não volta e
 *    nada é enviado. O resto → responde à fala nova.
 *
 * Os números são ponto de partida; aparelho, fone e ambiente mudam o resultado e só se
 * confirmam com microfone de verdade.
 */

export const TICK_MS = 50;

/** Fora da fala do Brain: os mesmos números da detecção de fala do original. */
export const LIMIAR_OUVINDO = 0.012;
export const MULTIPLICADOR_OUVINDO = 2.5;

/** Durante a fala do Brain: o piso e o quanto acima do fundo (eco incluído) a voz precisa estar. */
export const LIMIAR_TOCANDO = 0.045;
export const MULTIPLICADOR_TOCANDO = 4;
/** Fala contínua da pessoa, por cima da voz do Brain, para parar a voz. */
export const INTERRUPCAO_MINIMA_MS = 350;
/** Pausa entre sílabas que não zera a contagem. */
export const BURACO_TOLERADO_MS = 100;
/** O começo de cada fala do Brain só mede o eco (ninguém interrompe nos primeiros 400 ms). */
export const CALIBRACAO_MS = 400;

export interface EstadoDaEscuta {
    /** Nível de fundo (RMS), eco da voz do Brain incluído. */
    ruido: number;
    seguidoMs: number;
    buracoMs: number;
    calibradoMs: number;
}

export function escutaNova(ruido = 0.004): EstadoDaEscuta {
    return { ruido, seguidoMs: 0, buracoMs: 0, calibradoMs: 0 };
}

export function limiarDeFala(ruido: number, tocando: boolean): number {
    return tocando
        ? Math.max(LIMIAR_TOCANDO, ruido * MULTIPLICADOR_TOCANDO)
        : Math.max(LIMIAR_OUVINDO, ruido * MULTIPLICADOR_OUVINDO);
}

/**
 * Uma amostra do medidor enquanto o Brain fala. Devolve o estado novo (não altera o
 * recebido), se é para interromper agora e há quanto tempo a pessoa fala.
 */
export function avaliarDuranteAFala(estado: EstadoDaEscuta, rms: number, tickMs = TICK_MS): { estado: EstadoDaEscuta; interromper: boolean; seguidoMs: number } {
    if (estado.calibradoMs < CALIBRACAO_MS) {
        const ruido = estado.ruido * 0.85 + rms * 0.15;
        return { estado: { ruido, seguidoMs: 0, buracoMs: 0, calibradoMs: estado.calibradoMs + tickMs }, interromper: false, seguidoMs: 0 };
    }
    const alto = rms > limiarDeFala(estado.ruido, true);
    let { ruido, seguidoMs, buracoMs } = estado;
    if (alto) {
        seguidoMs += tickMs;
        buracoMs = 0;
    } else if (seguidoMs > 0) {
        buracoMs += tickMs;
        if (buracoMs > BURACO_TOLERADO_MS) {
            seguidoMs = 0;
            buracoMs = 0;
        }
    } else {
        /* Sem ninguém falando, o que chega é fundo e eco: o limiar acompanha. */
        ruido = ruido * 0.97 + rms * 0.03;
    }
    return { estado: { ruido, seguidoMs, buracoMs, calibradoMs: estado.calibradoMs }, interromper: seguidoMs >= INTERRUPCAO_MINIMA_MS, seguidoMs };
}

export type DesfechoDaInterrupcao = 'retomar' | 'parar' | 'responder';

const RUIDOS = new Set(['hm', 'hmm', 'hum', 'humm', 'ah', 'aah', 'aham', 'uh', 'uhm', 'eh', 'e', 'a', 'o', 'ha', 'an', 'mm', 'mmm', 'oh', 'ai', 'ue', 'tsc', 'pss', 'shh']);
const PARAR = new Set(['para', 'pare', 'parar', 'chega', 'basta', 'espera', 'espere', 'silencio', 'cala', 'stop', 'wait', 'enough', 'detente', 'esperate', 'alto']);
/* O Whisper inventa estas frases quando recebe silêncio ou música. */
const ALUCINACOES = /amara\.org|legendas? (pela|por|da)|obrigad[oa] por assistir|inscreva-se|thanks for watching|subt[ií]tulos? (por|realizados)|gracias por ver/i;

export function normalizarFala(texto: string): string {
    return texto
        .toLowerCase()
        .normalize('NFD')
        .replace(/\p{M}/gu, '')
        .replace(/[^\p{L}\p{N}\s]/gu, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

/** A transcrição repete as palavras que o Brain estava falando: é o eco dele, não a pessoa. */
export function ecoDaFala(transcricao: string, falaDoBrain: string): boolean {
    const doBrain = new Set(normalizarFala(falaDoBrain).split(' ').filter((p) => p.length > 2));
    const ditas = normalizarFala(transcricao).split(' ').filter((p) => p.length > 2);
    if (ditas.length < 2 || doBrain.size === 0) return false;
    const repetidas = ditas.filter((p) => doBrain.has(p)).length;
    return repetidas / ditas.length >= 0.7;
}

/**
 * O que fazer com o que foi dito por cima da voz do Brain (ou enquanto ele pensa, com
 * `falaDoBrain` vazia).
 */
export function desfechoDaInterrupcao(transcricao: string, falaDoBrain: string): DesfechoDaInterrupcao {
    const bruto = transcricao.trim();
    const palavras = normalizarFala(bruto).split(' ').filter(Boolean);
    const pedeParar = palavras.some((p) => PARAR.has(p));
    if (palavras.join('').length < 3 && !pedeParar) return 'retomar';
    if (/^[[(].*[\])]$/.test(bruto) || ALUCINACOES.test(bruto)) return 'retomar';
    if (palavras.every((p) => RUIDOS.has(p))) return 'retomar';
    if (pedeParar && palavras.length <= 3 && palavras.every((p) => PARAR.has(p) || RUIDOS.has(p) || p.length <= 3)) return 'parar';
    if (ecoDaFala(bruto, falaDoBrain)) return 'retomar';
    return 'responder';
}

/*
 * Encerrar a conversa falando (13/09/2026): "obrigado, não preciso de mais nada", "tchau",
 * "pode encerrar" — ou "não" logo depois de "precisa de mais alguma coisa?". Só frase curta e
 * feita de palavras de despedida: "encerra o atendimento da Clara" e "é só isso que tem na
 * agenda?" são pedidos, não despedida.
 */
const DESPEDIDA = /\b(tchau|xau|ate (mais|logo|a proxima)|bye|goodbye|adios|hasta (luego|pronto))\b/;
const FECHAMENTO = /\b(mais nada|nada mais|so isso|era isso|e isso|nao preciso|por hoje|por enquanto|that s all|nothing else|eso es todo|nada mas)\b/;
const AGRADECIMENTO = new Set(['obrigado', 'obrigada', 'brigado', 'brigada', 'valeu', 'agradeco', 'thanks', 'thank', 'gracias']);
const VOCABULARIO_DA_DESPEDIDA = new Set([
    ...AGRADECIMENTO, 'nao', 'preciso', 'de', 'mais', 'nada', 'so', 'isso', 'era', 'por', 'hoje', 'enquanto', 'tudo', 'certo', 'ta', 'bom',
    'entao', 'ok', 'vecchia', 'muito', 'pode', 'ja', 'ajudou', 'resolveu', 'tchau', 'xau', 'ate', 'logo', 'proxima', 'you', 'that', 's', 'all',
    'nothing', 'else', 'bye', 'goodbye', 'eso', 'es', 'todo', 'mas', 'adios', 'hasta', 'luego', 'pronto', 'no', 'si', 'la', 'el', 'um', 'uma',
]);
const ORDEM_DE_ENCERRAR = new Set(['encerra', 'encerrar', 'encerre', 'finaliza', 'finalizar', 'finalize', 'desliga', 'desligar', 'termina', 'terminar', 'end', 'cierra', 'finish']);
const COMPLEMENTO_DA_ORDEM = new Set([
    ...AGRADECIMENTO, 'pode', 'voz', 'conversa', 'chamada', 'isso', 'por', 'hoje', 'aqui', 'ja', 'favor', 'vecchia', 'ok', 'ta', 'entao', 'agora',
    'pra', 'para', 'mim', 'the', 'call', 'la', 'conversacion', 'ya',
]);
const RESPOSTA_NEGATIVA = new Set([...AGRADECIMENTO, 'nao', 'nada', 'no', 'nope', 'so', 'isso', 'por', 'enquanto', 'tudo', 'certo', 'ta', 'bom', 'ok', 'era', 'de', 'mais']);

export function pedeParaEncerrar(transcricao: string, contexto: { depoisDaPergunta?: boolean } = {}): boolean {
    const normal = normalizarFala(transcricao);
    const palavras = normal.split(' ').filter((p) => p && !RUIDOS.has(p));
    if (palavras.length === 0 || palavras.length > 10) return false;
    if (DESPEDIDA.test(normal) && palavras.length <= 6) return true;
    if (FECHAMENTO.test(normal) && palavras.every((p) => VOCABULARIO_DA_DESPEDIDA.has(p))) return true;
    if (palavras.length <= 4 && palavras.some((p) => ORDEM_DE_ENCERRAR.has(p)) && palavras.every((p) => ORDEM_DE_ENCERRAR.has(p) || COMPLEMENTO_DA_ORDEM.has(p))) return true;
    /* Logo depois de "precisa de mais alguma coisa?": "não", "não, obrigado", "tá tudo certo". */
    if (contexto.depoisDaPergunta && palavras.length <= 5 && palavras.every((p) => RESPOSTA_NEGATIVA.has(p))) {
        return palavras.some((p) => p === 'nao' || p === 'no' || p === 'nada' || AGRADECIMENTO.has(p)) || (palavras.includes('tudo') && palavras.includes('certo'));
    }
    return false;
}

/*
 * Resposta falada a uma ação que espera decisão na tela (B9, 13/09/2026). "Pode", "sim", "pode
 * agendar" → confirmar; "não", "agora não", "cancela", "deixa" → cancelar. O claro resolve aqui,
 * sem esperar; o que fica em dúvida ("bora nessa", "não precisa") vai ao modelo rápido
 * (`intencaoDaIa` lê a resposta dele); frase longa, com número ou com pedido junto ("pode, mas às
 * 15h") é conversa, não decisão.
 */
export type IntencaoSobreAcao = 'confirmar' | 'cancelar' | 'outra' | 'incerta';

const SIM = new Set([
    'sim', 'pode', 'podes', 'confirma', 'confirmo', 'confirmar', 'confirmado', 'confirme', 'ok', 'okay', 'beleza', 'blz', 'isso', 'claro',
    'certo', 'fechado', 'fechou', 'combinado', 'manda', 'mandar', 'faz', 'faca', 'fazer', 'agenda', 'agendar', 'agende', 'envia', 'enviar',
    'envie', 'cria', 'criar', 'crie', 'registra', 'registrar', 'segue', 'seguir', 'bora', 'vai', 'positivo', 'perfeito', 'exato', 'yes',
    'yeah', 'yep', 'sure', 'si', 'dale', 'adelante', 'hazlo', 'puedes',
]);
const NAO = new Set(['nao', 'no', 'nope', 'cancela', 'cancelar', 'cancele', 'cancelo', 'deixa', 'deixe', 'esquece', 'esqueca', 'negativo', 'nunca', 'nem', 'cancel', 'dont', 'olvidalo', 'cancelalo']);
const MIUDEZAS = new Set([
    'pra', 'para', 'voce', 'vc', 'ai', 'la', 'por', 'favor', 'agora', 'mesmo', 'ja', 'entao', 'ta', 'bom', 'tudo', 'bem', 'ser', 'aqui', 'me',
    'mim', 'nos', 'please', 'go', 'ahead', 'do', 'it', 'lo', 'um', 'uma', 'de', 'da', 'obrigado', 'obrigada', 'valeu', 'hoje', 'melhor',
    'depois', 'ver', 'essa', 'esse', 'assim', 'com', 'certeza', 'vamos', 'agendamento', 'mensagem', 'retorno', 'tarefa', 'lead', 'pagamento',
]);

export function intencaoSobreAcao(transcricao: string): IntencaoSobreAcao {
    const bruto = transcricao.trim();
    if (!bruto || /^[[(].*[\])]$/.test(bruto) || ALUCINACOES.test(bruto)) return 'outra';
    const normal = normalizarFala(bruto);
    const palavras = normal.split(' ').filter((p) => p && !RUIDOS.has(p));
    if (palavras.length === 0 || palavras.length > 6 || /\d/.test(normal)) return 'outra';
    if (/^ta( bom| certo| ok)?$/.test(palavras.join(' '))) return 'confirmar';
    const temNao = palavras.some((p) => NAO.has(p));
    const temSim = palavras.some((p) => SIM.has(p));
    if (temNao && temSim) return 'incerta';
    if (temNao) return palavras.every((p) => NAO.has(p) || MIUDEZAS.has(p)) ? 'cancelar' : 'incerta';
    if (temSim) return palavras.every((p) => SIM.has(p) || MIUDEZAS.has(p)) ? 'confirmar' : 'incerta';
    return palavras.length <= 3 ? 'incerta' : 'outra';
}

/** A resposta do modelo rápido (`brainTexto('intencao')`): só "confirmar" e "cancelar" decidem. */
export function intencaoDaIa(resposta: string): 'confirmar' | 'cancelar' | 'outra' {
    const palavra = normalizarFala(resposta).split(' ')[0] ?? '';
    if (palavra.startsWith('confirm')) return 'confirmar';
    if (palavra.startsWith('cancel')) return 'cancelar';
    return 'outra';
}
